export enum ClientError {
    INVALID_API_VERSION = 'Invalid API version',
    NOT_FOUND = 'Request not found',
    ENTITY_ERROR = 'Entity error',
    BAD_REQUEST = 'Bad request',
    REQUEST_FAILED = 'Request Failed',
    NO_CONNECTION = 'No connection',
    SERVER_ERROR = 'Server Error',
    INTERNAL_ERROR = 'Internal error',
    GENERAL_ERROR = 'General Error',
    GENERAL_REQUEST_ERROR = 'General request error',
    AUTHENTICATION_ERROR = 'Authentication Error',
    GATE_WAY_ERROR = 'Gate way error',
    RECORD_CONFLICT = 'Record conflict'
}

export type IdentificationTypeName = 'OpenMRS Identification Number' |
    'Old Identification Number' |
    'National id' |
    'ARV Number' |
    'z_deprecated Pre ART Number (Old format)' |
    'VHW ID' |
    'District TB Number' |
    'Dummy ID' |
    'z_deprecated EID Number' |
    'Unknown ID' |
    'MDR-TB Program Identifier' |
    'KS Number' |
    'z_deprecated PART Number' |
    'Diabetes Number' |
    'LAB IDENTIFIER' |
    'DS Number' |
    'Filing number' |
    'Archived filing number' |
    'HCC Number' |
    'Radiology Study Number' |
    'IVR Access Code' |
    'Pre-ART Number' |
    'Serial Number' |
    'HTC Identifier' |
    'ANC Connect ID' |
    'FUCHIA ID' |
    'DDE person document ID' |
    'Malawi National ID' |
    'District IPT Number' |
    'Legacy ARV Number'

export type UserRoleName = 'Anonymous' |
    'Provider' |
    'System Developer' |
    'Accompagnateur' |
    'Accompagnateur Leader' |
    'Adults' |
    'Clinician' |
    'Counselor' |
    'Data Assistant' |
    'Data Element Contributor' |
    'Data Manager' |
    'Doctor' |
    'General Registration Clerk' |
    'HMIS lab order' |
    'HSA' |
    'Informatics Manager' |
    'Lab' |
    'Medical Assistant' |
    'Nurse' |
    'Paediatrics' |
    'Pharmacist' |
    'Program Manager' |
    'Registration Clerk' |
    'Social Worker' |
    'SPINE clinician' |
    'Superuser' |
    '"Superuser,Superuser,"' |
    'Supervisor' |
    'Therapeutic Feeding Clerk' |
    'Vitals Clerk' |
    'Authenticated'

export interface UpdatedUserI {
    given_name?: string;
    family_name?: string;
    password?: string;
    roles?: UserRoleName[];
    programs?: number[];
    must_append_roles?: boolean;
}

export interface NewUserI {
    given_name: string;
    family_name: string;
    roles: UserRoleName[];
    username: string;
    password: string;
    programs?: number[];
}

export interface RequestDataI {
    url: string;
    config: Record<string, any>
}

export interface UserI {
    deactivated_on: string | null;
    user_id: number;
    creator: number;
    person_id: number;
    changed_by: number;
    date_changed: string;
    system_id: string;
    username: string;
    date_created: string;
    retired: number;
    retired_by: number | null;
    date_retired: string | null;
    retire_reason: string;
    uuid: string;
    roles: RoleI[];
    programs: any[]; // You might want to define a type for programs if needed
    person: PersonI;
}
  
export interface RoleI {
    role: string;
    description: string;
    uuid: string;
}

interface PersonAddressI {
    person_address_id: number;
    person_id: number;
    preferred: number;
    address1: null | string;
    address2: string;
    city_village: string;
    state_province: string;
    postal_code: null | string;
    country: null | string;
    latitude: null | string;
    longitude: null | string;
    creator: number;
    date_created: string;
    voided: number;
    voided_by: null | number;
    date_voided: null | string;
    void_reason: null | string;
    county_district: string;
    neighborhood_cell: string;
    region: null | string;
    subregion: null | string;
    township_division: string;
    uuid: string;
}

export interface PersonI {
    person_id: number;
    gender: string;
    birthdate: string | null;
    birthdate_estimated: number;
    dead: number;
    death_date: string | null;
    cause_of_death: string | null;
    creator: number;
    date_created: string;
    changed_by: number;
    date_changed: string;
    voided: number;
    voided_by: number | null;
    date_voided: string | null;
    void_reason: string | null;
    uuid: string;
    names: PersonNameI[];
    person_attributes: PersonAttributeI[];
    addresses: PersonAddressI[];
}

export interface PersonNameI {
    person_name_id: number;
    preferred: number;
    person_id: number;
    prefix: string | null;
    given_name: string;
    middle_name: string;
    family_name_prefix: string | null;
    family_name: string;
    family_name2: string | null;
    family_name_suffix: string | null;
    degree: string | null;
    creator: number;
    date_created: string;
    voided: number;
    voided_by: number | null;
    date_voided: string | null;
    void_reason: string | null;
    changed_by: number | null;
    date_changed: string | null;
    uuid: string;
}

export interface RequestBodyI {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    body?: string
}

export interface AuthorizationDataI {
    token: string;
    expiry_time: string;
    user: UserI;
}

export interface DDESearchDemographicsI {
    given_name: string;
    family_name: string;
    gender: string;
}

export interface DDESearchByDemographicsResponse {
    locals: PatientI[];
    remotes: PatientI[];
}

export interface DDEclientMergeDataI {
    patient_id: number;
    doc_id: string;
}

/**
 * Data that is saved in session storage for persitence
 */ 
export interface ApiDataI {
    host: string;
    token: string;
    tokenExpiryDate: string;
    userID: string;
    personID: string;
    username: string;
    userRoles: string;
}

/**
 * When making API calls, this object will be returns
 */
export interface JsonRequestResponse<T = any> {
    /**
     * For successful API connections, ok will be true
     */
    ok: boolean;
    /**s
     * Retrieve client error type. Useful for handling specific errors
     */
    clientErrorType?: ClientError;
    /**
     * Generated error message
     */
    errorMessage?: string;
    /**
     * Response as returned by fetch request
    */
    httpStatusResponse?: number;
    /**
     * Any data returned by the API
    */
    data?: T;
}

export interface ObsValueI {
    concept_id: number;
    value_text?: string;
    value_coded?: number;
    value_datetime?: string;
    value_modifier?: string | null;
    value_boolean?: string;
    value_numeric?: number;
    obs_group_id?: number;
    obs_datetime?: string;
}

export interface NewObsI {
    encounter_id: number;
    observations: Array<ObsValueI>;
}

export interface NewPatientI {
    given_name?: string;
    family_name?: string;
    gender?: string;
    birth_date?: string;
    home_district?: number;
    birthdate_estimated?: boolean;
    home_traditional_authority?: number;
    home_village?: number;
    current_district?: number;
    current_village?: number;
    current_traditional_authority?: number;
    cell_phone_number?: string;
    landmark?: string;
}

export interface NewPersonAttributeI {
    person_id: number;
    person_attribute_type_id: number;
    value: number | string;
}

export interface PersonAttributeTypeI {
    person_attribute_type_id: number;
    name: string;
    description: string;
    format: string;
    foreign_key: null;
    searchable:number;
    creator: number;
    date_created: Date;
    changed_by: number;
    date_changed: Date;
    retired: number;
    retired_by: null;
    date_retired: null;
    retire_reason: null;
    edit_privilege:null;
    uuid: string;
    sort_weight: number;
}

export interface PersonAttributeI {
    person_attribute_id?: number;
    person_id: number;
    value: string;
    person_attribute_type_id: number;
    creator?: number;
    date_created?: Date;
    changed_by?: null;
    date_changed?: null;
    voided?: number;
    voided_by?: null;
    date_voided?: null;
    void_reason?: null;
    uuid?: string;
    type?: PersonAttributeTypeI;
}

export interface NewPersonI {
    given_name: string;
    family_name: string;
    middle_name?: string;
    gender: string;
    birthdate: string;
    birthdate_estimated: boolean;
    home_district: string;
    home_traditional_authority: string;
    home_village: string;
    current_district: string;
    current_traditional_authority: string;
    current_village: string;
    landmark: string;
    cell_phone_number: string;
    occupation: string;
}

export interface ModifyPersonI {
    program_id?: number;
    given_name?: string;
    family_name?: string;
    gender?: string;
    birthdate?: string;
    birthdate_estimated?: boolean;
    home_district?: string;
    home_traditional_authority?: string;
    home_village?: string;
    current_district?: string;
    current_traditional_authority?: string;
    current_village?: string;
    landmark?: string;
    cell_phone_number?: string;
    occupation?: string;
}

export interface NewStateI {
    state: number;
    date: string;
}

export interface NewTestsI {
    concept_id: number
}

export interface NewOrderI {
    accession_number : string;
    encounter_id: number;
    tests: NewTestsI[];
    reason_for_test_id: string;
    target_lab: string;
    date: string;
    requesting_clinician: string;
    specimen?: {
        concept_id: number
    }
}

export interface NewLabOrderI {
    encounter_id: number;
    order: NewOrderI[];
}

export interface DrugI{
    drug_inventory_id: number;
    dose: number;
    drug?: any;
    dosage_struct?: any;
    order?: any;
    equivalent_daily_dose: number;
    frequency: string;
    start_date: string;
    auto_expire_date: string;
    instructions: string;
    units: string;
    quantity?: number;
}

export interface PatientI {
    patient_id: number;
    tribe: string|null;
    creator: string;
    date_created: string;
    changed_by: number;
    date_changed: string;
    voided: number;
    voided_by: string|null;
    date_voided: string|null;
    void_reason: string|null;
    merge_history: any[];
    tpt_status: {
        tpt: string|null;
        completed: boolean;
        tb_treatment: boolean;
        tpt_init_date: string|null;
        tpt_complete_date: string|null;
        tpt_end_date: string|null
    };
    art_start_date: string;
    person: PersonI;
    patient_identifiers: PatientIdentifierI[];
}

export interface PatientIdentifierI {
    patient_identifier_id: number;
    patient_id: number;
    identifier: string;
    identifier_type: number;
    preferred: number;
    location_id: number;
    creator: number;
    date_created: string;
    voided: number;
    voided_by: number|null;
    date_voided: string|null;
    void_reason: string|null;
    uuid: string;
}

export interface DemographicSearchParamsI {
    given_name?: string;
    family_name?: string;
    gender?: string;
    middle_name?: string; 
    birthdate?: string;
}

export interface RelationshipTypeI {
    relationship_type_id: number;
    a_is_to_b: string;
    b_is_to_a: string;
    preferred: number;
    weight: number;
    description: string;
    creator: number;
    date_created: string;
    uuid: string;
    retired: boolean;
    retired_by: null | number;
    date_retired: null | string;
    retire_reason: null | string;
}

export interface RelationI {
    relationship_id: number;
    person_a: number;
    relationship: number;
    person_b: number;
    creator: number;
    date_created: string;
    voided: number;
    voided_by: null | number;
    date_voided: null | string;
    void_reason: null | string;
    uuid: string;
    type: RelationshipTypeI;
    relation: PersonI;
}

export interface RegionI {
    region_id: number;
    name: string;
    creator: number;
    date_created: string;
    retired: boolean;
    retired_by: null | number;
    date_retired: null | string;
    retire_reason: null | string;
}

export interface DistrictI {
    district_id: number;
    name: string;
    region_id: number;
    creator: number;
    date_created: string;
    retired: boolean;
    retired_by: null | number;
    date_retired: null | string;
    retire_reason: null | string;
}

export interface TAI {
    traditional_authority_id: number;
    name: string;
    district_id: number;
    creator: number;
    date_created: string;
    retired: boolean;
    retired_by: null | number;
    date_retired: null | string;
    retire_reason: null | string;
}

export interface VillageI {
    village_id: number;
    name: string;
    traditional_authority_id: number;
    creator: number;
    date_created: string;
    retired: boolean;
    retired_by: null | number;
    date_retired: null | string;
    retire_reason: null | string;
}

export interface LocationI {
    location_id: number;
    name: string;
    description: string;
    address1: string;
    address2: string;
    city_village: string;
    state_province: string;
    postal_code: string;
    country: string;
    latitude: string;
    longitude: string;
    creator: number;
    date_created: string;
    county_district: null | string;
    neighborhood_cell: null | string;
    region: null | string;
    subregion: null | string;
    township_division: null | string;
    retired: boolean;
    retired_by: null | number;
    date_retired: null | string;
    retire_reason: null | string;
    location_type_id: null | number;
    parent_location: null | number;
    uuid: string;
    district: string;
}

export interface NewDrugOrderI {
    encounter_id: number;
    drug_orders: DrugI[];
}

export interface LoginAuthenticationResponse {
    authorization : AuthorizationDataI;
}

export interface UserActivationI {
    message: string[];
    user: UserI
}

export interface UserPropertyI {
    user_id: number;
    property: string;
    property_value: string;
}

export interface NewGlobalPropertyI {
    property: string;
    property_value: string;
    description: null | string;
    uuid: string;
}

export interface ObsFiltersI {
    person_id?: number;
    concept_id?: number;
    encounter_id?: number;
    order_id?: number;
    value_coded?: number;
    value_datetime?: string;
    value_numeric?: number;
    accession_number?: number; 
    value_text?: string;
    program_id?: number;
    date?: string;
    start_date?: string;
    end_date?: string;

}

export interface ConceptNameI {
    concept_id: number;
    name: string;
    locale: string;
    creator: number;
    date_created: string;
    concept_name_id: number;
    voided: number;
    voided_by: any;
    date_voided: any;
    void_reason: any;
    uuid: string;
    concept_name_type: string;
    locale_preferred: number;
  }
  
export interface ConceptI {
    concept_id: number;
    retired: number;
    short_name: any;
    description: any;
    form_text: any;
    datatype_id: number;
    class_id: number;
    is_set: number;
    creator: number;
    date_created: string;
    default_charge: any;
    version: string;
    changed_by: any;
    date_changed: any;
    retired_by: any;
    date_retired: any;
    retire_reason: any;
    uuid: string;
    concept_names: ConceptNameI[];
}
  
export interface ObservationI {
    obs_id: number;
    person_id: number;
    concept_id: number;
    encounter_id: number;
    order_id: any;
    obs_datetime: string;
    location_id: any;
    obs_group_id: any;
    accession_number: any;
    value_group_id: any;
    value_boolean: any;
    value_coded: any;
    value_coded_name_id: any;
    value_drug: any;
    value_datetime: any;
    value_numeric: number;
    value_modifier: any;
    value_text: any;
    date_started: any;
    date_stopped: any;
    comments: any;
    creator: number;
    date_created: string;
    voided: number;
    voided_by: any;
    date_voided: any;
    void_reason: any;
    value_complex: any;
    uuid: string;
    concept: ConceptI;
    children: ObservationI[];
}

export interface EncounterFiltersI {
    program_id?: number;
    patient_id?: number;
    location_id?: number;
    encounter_type_id?: number;
    date?: string;
    page?: number;
    page_size?: number;
}

export interface EncounterTypeI {
    encounter_type_id: number;
    name: string;
    description: string;
    creator: number;
    date_created: string;
    retired: number;
    retired_by: null | any;
    date_retired: null | any;
    retire_reason: null | any;
    uuid: string;
}

export interface ProgramI {
    program_id: number;
    concept_id: number;
    creator: number;
    date_created: string;
    changed_by: number;
    date_changed: string;
    retired: number;
    name: string;
    description: string;
    uuid: string;
}

export interface NewEncounterI {
    encounter_type_id: number; 
    patient_id: number; 
    program_id: number;
    provider_id?: number;
    encounter_datetime?: string;
}

export interface EncounterI {
    encounter_id: number;
    encounter_type: number;
    patient_id: number;
    provider_id: number;
    location_id: number;
    form_id: null | any;
    encounter_datetime: string;
    creator: number;
    date_created: string;
    voided: number;
    voided_by: null | any;
    date_voided: null | any;
    void_reason: null | any;
    uuid: string;
    changed_by: number;
    date_changed: string;
    program_id: number;
    type: EncounterTypeI;
    patient: {
      patient_id: number;
      tribe: null | any;
      creator: number;
      date_created: string;
      changed_by: null | any;
      date_changed: null | any;
      voided: number;
      voided_by: null | any;
      date_voided: null | any;
      void_reason: null | any;
    };
    location: LocationI;
    provider: PersonI;
    program: ProgramI;
    observations: ObservationI[];
}

export interface EncounterCountParamsI {
    encounter_types: number[];
    program_id?: number;
    date?: string;
}

export interface UpdateEncounterI {
    type_id?: number;
    patient_id?: number;
    provider_id?: number;
    encounter_datetime?: string;
}

export interface UserFiltersI {
    page_size?: number;
    page?: number;
    role?: UserRoleName;
}

export interface ConceptFiltersI {
    name?: string;
    set?: string;
    page_size?: number;
    page?: number;
}